/**
 * AudioRecover — auto-repair corrupted audio files using the browser's native
 * media decoder.
 *
 * Background — bare FFmpeg WASM is strict about MP4/M4A container structure.
 * If the moov atom is missing (interrupted recording, truncated upload, dying
 * phone) FFmpeg dies with "moov atom not found" + Aborted(). Browser media
 * stacks (Chromium, WebKit) handle this every day for video-on-demand and
 * have far more lenient parsers. This module funnels failed-by-FFmpeg files
 * through `decodeAudioData` to recover what's salvageable, then re-encodes
 * to a clean PCM WAV that FFmpeg accepts on the second try.
 *
 * Wired into FFmpegProcessor.process via ffmpeg-auto-guard.js — every audio
 * tool gets recovery for free. No per-tool integration needed.
 *
 * Coverage from prod telemetry (audio-enhancer last 3 days): 6/9 of all
 * `all_attempts_failed` events match the pattern this module recovers.
 * Industry comparable: YouTube, Apple Music, BandLab use the same browser-
 * decode-as-fallback strategy for user-uploaded broken audio.
 */
(function() {
'use strict';

// Cap input bytes — repaired WAV is typically 5–10× the compressed input,
// and we don't want a 1-hour AAC at 64 kbps (~30 MB) to balloon into a
// 600 MB WAV that OOMs the tab. Scaled by reported device memory; a 2 GB
// Android tablet gets ~38 MB, an 8 GB desktop gets ~150 MB. Hard cap 200 MB.
function maxInputBytes() {
    var dmGB = navigator.deviceMemory || 4;
    // Worst-case PCM expansion ~ 8× compressed input. Keep peak under 15% of
    // device RAM to leave room for FFmpeg state on the retry.
    var safeWavMB = dmGB * 1024 * 0.15;
    var safeInputMB = Math.min(safeWavMB / 8, 200);
    return safeInputMB * 1024 * 1024;
}

var DECODE_TIMEOUT_MS = 30000;

// Per-File memoization. WeakMap means we don't have to think about cleanup —
// when the original File is GC'd, the repaired entry goes with it.
var repairCache = typeof WeakMap !== 'undefined' ? new WeakMap() : null;
// String-identity memo alongside the WeakMap. The pipeline re-wraps files
// (FileBytes.asFile, defensive new File([...]) copies in tools) so the SAME
// user file arrives here as a DIFFERENT object each attempt — the WeakMap
// always missed and every mode-click re-ran the identical failing recovery.
// Field session 2026-07-26: audio_recover_attempted ×6 for one
// 23 MB m4a, identical "Invalid sample size" every time. name|size|lastModified
// is the same identity key FileBytes uses. Bounded: cleared past 8 entries.
var _resultByKey = new Map();
function _fileKey(f) {
    if (!f) return null;
    try { return (f.name || '') + '|' + (f.size || 0) + '|' + (f.lastModified || 0); } catch (_) { return null; }
}

// Audio extensions we'll attempt repair on. Video extensions are deliberately
// excluded — the user uploaded a video; converting it to WAV would discard
// the video track entirely. Tools that handle video paths (mute-video, repair-
// video, video-trim) keep their existing recovery logic.
var AUDIO_EXTENSIONS = [
    'mp3','m4a','m4b','aac','wav','wave','flac','ogg','oga','opus',
    'wma','aiff','aif','caf','3gp','3gpp','amr'
];

/* ⚠️ КОНТЕЙНЕРЫ, КОТОРЫЕ НЕСУТ ЗВУК, НО НАЗЫВАЮТСЯ ВИДЕО.
   Раньше их отсекало насмерть: `isAudioInput` спрашивал «называется ли это
   аудио», а решать надо «есть ли внутри звук, который мы умеем достать».
   Замер на проде за 60 дней: из 744 попыток декода, сорвавшихся хотя бы на
   одном тире, 243 (33%) пришли с видео-контейнером, и 163 из них — Safari,
   то есть айфонные .mov прямо из плёнки. Все они получали от третьего тира
   «AudioRecover declined to retry» и НИ ОДНОЙ строки о причине: отказ стоял
   до всякой телеметрии.

   Что важнее — отказ был не по способности. Проверено на одном и том же файле
   байт в байт (tests/playwright/audio-recover-takes-video-containers.mjs):
   как `video/quicktime` → null; те же байты как `audio/mp4` с расширением
   .m4a → восстановлен в 529 244 байта чистого WAV. Механизм умел всегда,
   его не пускала проверка имени.

   Размерный потолок (maxInputBytes) при этом остаётся и работает как прежде —
   мы снимаем фильтр по НАЗВАНИЮ, а не защиту по весу. */
var VIDEO_CONTAINER_EXTENSIONS = [
    'mp4','m4v','mov','qt','webm','mkv','avi','ogv','ts','m2ts','mts','flv','wmv','3g2'
];

// Failure signatures that are SAFE to retry through the browser decoder.
// We intentionally do NOT retry on:
//   - real OOM ("out of memory", "Cannot allocate", "RangeError")
//   - cancellation ("called FFmpeg.terminate()")
//   - codec-not-found (browser won't have the codec either)
// because those won't be helped by re-decoding via Web Audio.
var CONTAINER_PATTERNS = [
    /moov atom not found/i,
    /Invalid data found when processing input/i,
    /Header missing/i,
    /signature mismatch/i,
    /truncated/i,
    /incomplete/i,
    /premature/i,
    /Could not find tag/i,
    /Format .+ detected only with low score/i
];

function ae(type, payload) {
    try { if (window.__ae) window.__ae(type, payload || {}); } catch(_){}
}

function stage(name, payload) {
    try { if (window.__stage) window.__stage(name, payload || {}); } catch(_){}
}

/* «Может ли в этом файле быть звук, который мы достанем» — а НЕ «называется
   ли он аудио». Видео-контейнер несёт звуковую дорожку, и третий тир умеет её
   вытащить (доказано замером, см. VIDEO_CONTAINER_EXTENSIONS выше). Имя
   функции оставлено прежним: её экспортируют наружу и зовёт
   ffmpeg-auto-guard.js, а переименование ради точности сломало бы вызов. */
function isAudioInput(file) {
    if (!file || !(file instanceof File || file instanceof Blob)) return false;
    var type = (file.type || '').toLowerCase();
    if (type.indexOf('audio/') === 0) return true;
    if (type.indexOf('video/') === 0) return true;
    if (!file.name) return false;
    var ext = file.name.split('.').pop();
    if (!ext || ext === file.name) return false;
    ext = ext.toLowerCase();
    return AUDIO_EXTENSIONS.indexOf(ext) !== -1
        || VIDEO_CONTAINER_EXTENSIONS.indexOf(ext) !== -1;
}

function isContainerError(err) {
    if (!err) return false;
    var msg = (err.message || String(err) || '');
    // Cancellation — never recover, user pressed Cancel
    if (/called\s+ffmpeg\.terminate|terminate\(\)/i.test(msg)) return false;
    // Real OOM signals — Web Audio decoder won't help with a memory wall
    if (/out of memory|cannot allocate|allocation failed|memory access out of bounds|RangeError/i.test(msg)) return false;
    for (var i = 0; i < CONTAINER_PATTERNS.length; i++) {
        if (CONTAINER_PATTERNS[i].test(msg)) return true;
    }
    return false;
}

// Scan the file's first 512 KB and last 512 KB for moov atom. MP4/M4A spec
// allows moov anywhere in the file; in practice it's at the start (streaming-
// optimized, iOS Voice Memos, faststart-encoded) or at the end (Android
// recorder default, FFmpeg default). If neither location has 'moov', the file
// is unrecoverable through any browser-native path — empirically tested
// against decodeAudioData / HTMLAudioElement / MediaSource / WebCodecs in
// tests/playwright/audio-recover-tier4-research.mjs (all failed). Caller
// should surface a specific UX hint instead of a generic recovery failure.
async function detectMoovMissing(file) {
    if (!file || !file.size) return false;
    // EDGE_BYTES was 512 KB pre-2026-06-22 — too narrow. User olgs-ra@mail.ru
    // reported HER OWN /repair-audio output (58.6 MB m4a) being rejected by
    // /trim's Tier 0 probe. Root cause: FFmpeg without -movflags +faststart
    // writes the moov atom at the END of an MP4 container; the moov box
    // itself grows to ~600 KB on a 1-hour audio track, so its starting
    // ascii fingerprint can land OUTSIDE the last 512 KB window
    // (reproduced 2026-06-22: 1-hour AAC m4a, moov starts at offset
    // 57,738,573 of 58,359,787; last 512 KB starts at 57,835,499). The
    // companion fix is `-movflags +faststart` in FFmpegProcessor.process so
    // moov goes at the HEAD of every MP4-family output we emit. Bumping
    // edge to 4 MB adds 8 MB of file-bytes reads on the rare moov-at-end
    // input but catches the long-tail of moov boxes that grew past 512 KB.
    // 4 MB chosen because the largest m4a moov box we've measured in
    // prod is ~3.5 MB (4 h talk-radio recordings on iPhone Voice Memos
    // with marker chapters).
    var EDGE_BYTES = Math.min(4 * 1024 * 1024, file.size);
    try {
        var headBuf = await file.slice(0, EDGE_BYTES).arrayBuffer();
        if (containsAscii4cc(headBuf, 'moov')) return false;
        // Head covered the WHOLE file → verdict is final. The old condition
        // here was `size <= EDGE_BYTES * 2` — wrong: for files in
        // (EDGE, 2×EDGE] the head only saw the first EDGE bytes but we
        // returned "missing" WITHOUT the tail check → false-positive
        // «файл повреждён» for any 4–8 MB m4a with moov at the end (e.g.
        // hour-long voice memos from non-faststart encoders). Caught
        // 2026-07-08 by audio-decoder-adaptive-timeouts.mjs case (E) on a
        // real 7.3 MB ffmpeg-default m4a. (In the 512 KB-edge era the same
        // logic falsely rejected 0.5–1 MB moov-at-end files.)
        if (file.size <= EDGE_BYTES) return true;
        var tailBuf = await file.slice(file.size - EDGE_BYTES).arrayBuffer();
        if (containsAscii4cc(tailBuf, 'moov')) return false;
        return true;  // moov nowhere to be found
    } catch(_) { return false; }
}

// Audio-only extensions — used to route the recovery hint between
// /repair-audio (audio container damaged, no reference needed) and
// /repair-video (video container damaged, reference file needed for
// moov reconstruction). Video extensions in AUDIO_EXTENSIONS upstream
// (3gp/3gpp) are intentionally NOT included here — those need video tooling.
var AUDIO_ONLY_EXTENSIONS = ['mp3','m4a','m4b','aac','wav','wave','flac','ogg','oga','opus','wma','aiff','aif','caf','amr'];

function isAudioOnlyExt(ext) {
    return AUDIO_ONLY_EXTENSIONS.indexOf((ext || '').toLowerCase()) !== -1;
}

// Stash a File in IndexedDB so the target tool can pick it up via
// ?transfer=1. Mirrors the pattern in repair-audio.blade.php and the
// audio-enhancer transfer-receive at the bottom of that file. Async,
// returns a Promise so the caller can navigate after the put commits.
function stashForTransfer(file) {
    return new Promise(function (resolve) {
        try {
            var req = indexedDB.open('timbrica_transfer', 1);
            req.onupgradeneeded = function (e) {
                try { e.target.result.createObjectStore('files'); } catch (_) {}
            };
            req.onsuccess = function (e) {
                var db = e.target.result;
                try {
                    var tx = db.transaction('files', 'readwrite');
                    tx.objectStore('files').put({
                        name: file.name, type: file.type, data: file
                    }, 'pending');
                    tx.oncomplete = function () { db.close(); resolve(true); };
                    tx.onerror    = function () { db.close(); resolve(false); };
                } catch (_) { try { db.close(); } catch(_){}; resolve(false); }
            };
            req.onerror = function () { resolve(false); };
        } catch (_) { resolve(false); }
    });
}

// Show a one-per-session actionable hint when we detect a definitively
// unrecoverable file. Routes:
//   - audio-only ext  → /repair-audio   (offers one-click transfer-and-go)
//   - video / other   → /repair-video   (requires reference file, no transfer)
// 9 locales — must mirror config('app.supported_locales').
var hintShownThisSession = false;
function showUnrecoverableHint(reason, file) {
    if (hintShownThisSession) return;
    if (typeof window.__toast !== 'function') return;
    hintShownThisSession = true;
    var loc = (window.__LOCALE || 'en').substring(0, 2);
    var ext = '';
    try { ext = ((file && file.name) || '').split('.').pop().toLowerCase(); } catch(_){}
    var isAudio = isAudioOnlyExt(ext);
    var locale = (window.__LOCALE || 'en').toLowerCase().split('-')[0];

    // Audio path — actionable, transfers to /repair-audio with one click.
    var L_AUDIO = {
        en: { msg: 'This file is broken (missing index / corrupted container). Our Repair Audio tool can rebuild the header and recover what is salvageable.', cta: 'Open Repair Audio' },
        ru: { msg: 'Файл повреждён (отсутствует заголовок / битый контейнер). Инструмент «Восстановление аудио» умеет перестроить заголовок и спасти то, что можно прочитать.', cta: 'Открыть Восстановление аудио' },
        ko: { msg: '이 파일이 손상되었습니다(인덱스 없음 / 컨테이너 깨짐). 오디오 복구 도구가 헤더를 재구성하고 복구 가능한 부분을 살릴 수 있습니다.', cta: '오디오 복구 열기' },
        ar: { msg: 'هذا الملف تالف (الفهرس مفقود / الحاوية معطوبة). تستطيع أداة «إصلاح الصوت» إعادة بناء الترويسة واستخلاص ما يمكن استرداده.', cta: 'فتح إصلاح الصوت' },
        id: { msg: 'File ini rusak (indeks hilang / kontainer korup). Alat Perbaikan Audio dapat membangun ulang header dan menyelamatkan apa yang bisa dipulihkan.', cta: 'Buka Perbaikan Audio' },
        pt: { msg: 'Este arquivo está corrompido (índice ausente / contêiner quebrado). A ferramenta Reparar Áudio pode reconstruir o cabeçalho e recuperar o que for possível.', cta: 'Abrir Reparar Áudio' },
        de: { msg: 'Diese Datei ist beschädigt (Index fehlt / Container kaputt). Das Werkzeug Audio reparieren kann den Header neu aufbauen und retten, was lesbar ist.', cta: 'Audio reparieren öffnen' },
        es: { msg: 'Este archivo está dañado (índice ausente / contenedor corrupto). La herramienta Reparar Audio puede reconstruir la cabecera y recuperar lo que sea salvable.', cta: 'Abrir Reparar Audio' },
        fr: { msg: "Ce fichier est endommagé (index manquant / conteneur corrompu). L'outil Réparer l'audio peut reconstruire l'en-tête et récupérer ce qui peut l'être.", cta: "Ouvrir Réparer l'audio" },
        uk: { msg: 'Файл пошкоджено (немає покажчика / зіпсований контейнер). Інструмент «Відновлення аудіо» вміє перебудувати заголовок і врятувати те, що ще читається.', cta: 'Відкрити відновлення аудіо' },
        tr: { msg: 'Bu dosya bozuk (dizin eksik / kapsayıcı hasarlı). Ses Onarma aracımız üstbilgiyi yeniden oluşturabilir ve kurtarılabilecek kısmı geri getirebilir.', cta: 'Ses Onarmayı Aç' }
    };

    // Video path — text-only suggestion, /repair-video needs a healthy
    // reference file from the same device so the auto-transfer is the wrong
    // affordance here. Keep as plain warning toast.
    var L_VIDEO = {
        en: 'This file is missing its index header (moov atom) — usually because the recording was cut off (battery died, app crashed). Try the /repair-video tool with a healthy file from the same device as a reference.',
        ru: 'У файла отсутствует служебный заголовок (moov atom) — обычно это случается когда запись прервалась (села батарея, закрылось приложение). Попробуйте инструмент /repair-video — он умеет восстанавливать по образцу нормальной записи с того же устройства.',
        ko: '이 파일은 인덱스 헤더(moov atom)가 없습니다 — 보통 녹음이 중단된 경우(배터리 방전, 앱 종료) 발생합니다. 같은 기기의 정상 녹음 파일을 기준으로 /repair-video 도구를 사용해 보세요.',
        ar: 'يفتقد هذا الملف رأس الفهرس (moov atom) — يحدث عادة عند انقطاع التسجيل (نفاد البطارية، تعطل التطبيق). جرب أداة /repair-video مع ملف سليم من نفس الجهاز كمرجع.',
        id: 'File ini kehilangan header indeks (moov atom) — biasanya terjadi ketika rekaman terputus (baterai habis, aplikasi crash). Coba alat /repair-video dengan file sehat dari perangkat yang sama sebagai referensi.',
        pt: 'Este arquivo está sem o cabeçalho de índice (moov atom) — geralmente quando a gravação foi interrompida (bateria, app fechou). Tente a ferramenta /repair-video com um arquivo íntegro do mesmo dispositivo como referência.',
        de: 'Diesem Datei fehlt der Index-Header (moov atom) — meist wenn die Aufnahme unterbrochen wurde (Akku, App-Absturz). Versuchen Sie /repair-video mit einer intakten Datei desselben Geräts als Vorlage.',
        es: 'A este archivo le falta la cabecera de índice (moov atom) — suele pasar cuando la grabación se interrumpió (batería, app cerrada). Prueba la herramienta /repair-video con un archivo sano del mismo dispositivo como referencia.',
        fr: "Ce fichier n'a pas d'en-tête d'index (moov atom) — typiquement parce que l'enregistrement a été coupé (batterie, app fermée). Essayez l'outil /repair-video avec un fichier sain du même appareil comme référence.",
        uk: 'У файлі немає службового заголовка (moov atom) — зазвичай так буває, коли запис обірвався (сів акумулятор, застосунок аварійно закрився). Спробуйте інструмент /repair-video, узявши за зразок цілий файл з того самого пристрою.',
        tr: 'Bu dosyada dizin üstbilgisi (moov atom) eksik — genellikle kayıt yarıda kesildiğinde olur (pil bitti, uygulama çöktü). Aynı cihazdan sağlam bir dosyayı referans olarak kullanarak /repair-video aracını deneyin.'
    };

    try {
        if (isAudio && file) {
            var entry = L_AUDIO[locale] || L_AUDIO.en;
            window.__toast(entry.msg, 'warning', {
                action: {
                    label: entry.cta,
                    onclick: function () {
                        stashForTransfer(file).then(function () {
                            window.location.href = '/' + locale + '/repair-audio?transfer=1';
                        });
                    }
                }
            });
        } else {
            var pool = L_VIDEO[locale] || L_VIDEO.en;
            window.__toast(pool, 'warning');
        }
    } catch (_) {}
}

function containsAscii4cc(arrayBuffer, fourCC) {
    if (fourCC.length !== 4) return false;
    var u8 = new Uint8Array(arrayBuffer);
    var b0 = fourCC.charCodeAt(0), b1 = fourCC.charCodeAt(1),
        b2 = fourCC.charCodeAt(2), b3 = fourCC.charCodeAt(3);
    var end = u8.length - 3;
    for (var i = 0; i < end; i++) {
        if (u8[i] === b0 && u8[i+1] === b1 && u8[i+2] === b2 && u8[i+3] === b3) return true;
    }
    return false;
}

/* __pcm16:begin
 * float → 16 бит. ОДИН квантователь сайта: этот блок одинаков (с точностью
 * до отступа) в audio-pipeline.js, media-pipeline/audio-native.js,
 * audio-post.js, audio-output-sink.js, audio-recover.js и timbrica-core.js
 * (там — window.__pcm16Quantizer для тулов со своим Int16; сторож —
 * tests/js/pcm16-quantize.test.mjs). Замеры и отвергнутые варианты —
 * docs/why/pcm16-quantize.md.
 *
 * pcm16Quantizer(dither) → q(src, n, dst, at, stride): n выборок канала src
 * пишутся в dst[at], dst[at + stride], … (Int16Array, каналы чередуются).
 *  · Масштаб — как у декодеров Chrome и Firefox (минус ×32768, плюс ×32767):
 *    нетронутый 16-битный звук возвращается бит-в-бит. Прежнее отрезание
 *    `| 0` меняло 27–30 % его выборок на единицу к нулю.
 *  · Блок из 1024 выборок, целиком лежащий на 16-битной сетке (нетронутый
 *    звук, цифровая тишина, выход декодеров с делением на 32768), копируется
 *    точно — без шума и без округления мимо.
 *  · dither=true — TPDF ±1 младший бит (готовый файл: звук тише младшего бита
 *    уходит в шум, а не в тишину и не в гармоники).
 *  · dither=false — округление «половина к чётному», без шума: для того, что
 *    ещё будут складывать (стемы) — сумма четырёх стемов на 9,6 дБ тише по шуму.
 *  · Зерно постоянное: один и тот же вход — одни и те же байты.
 */
function pcm16Quantizer(dither) {
    var x = 0x9E3779B9, TOL = 1 / 256, BLOCK = 1024;
    // Весь блок лежит на сетке ОДНОГО декодера (плюс ×pos: 32767 — Chrome и
    // Firefox, 32768 — ffmpeg)? Тогда это нетронутый звук: пишем точно.
    // Сетка выбирается на блок: по выборке выход ffmpeg у полной шкалы
    // (32640…32767) ложно садился на сетку 32767 и терял единицу.
    function exact(src, b, e, dst, at, stride, pos) {
        for (var i = b, w = at + b * stride; i < e; i++, w += stride) {
            var s = src[i];
            if (s !== s) s = 0;
            var v = s < 0 ? s * 32768 : s * pos, k = Math.floor(v + 0.5), d = v - k;
            if (d > TOL || d < -TOL) return false;
            dst[w] = k > 32767 ? 32767 : (k < -32768 ? -32768 : k);
        }
        return true;
    }
    return function (src, n, dst, at, stride) {
        // Состояние генератора — в локальной на время вызова: через замыкание
        // горячий цикл вдвое-вчетверо медленнее (замер в Chrome 156).
        var b, e, i, w, s, v, k, r = x, tri = dither;
        for (b = 0; b < n; b += BLOCK) {
            e = b + BLOCK < n ? b + BLOCK : n;
            if (exact(src, b, e, dst, at, stride, 32767) || exact(src, b, e, dst, at, stride, 32768)) continue;
            for (i = b, w = at + b * stride; i < e; i++, w += stride) {
                s = src[i];
                if (s !== s) s = 0;
                v = s < 0 ? s * 32768 : s * 32767;
                if (tri) {
                    r ^= r << 13; r ^= r >>> 17; r ^= r << 5;
                    k = Math.floor(v + ((r & 0xFFFF) + ((r >>> 16) & 0xFFFF)) * 1.52587890625e-5 - 0.5);  // ×2^-16
                } else {
                    k = Math.floor(v + 0.5);
                    if (k - v === 0.5 && (k & 1)) k--;
                }
                dst[w] = k > 32767 ? 32767 : (k < -32768 ? -32768 : k);
            }
        }
        x = r;
    };
}
/* __pcm16:end */

// 16-bit PCM WAV encoder. Pure JS, ~50 lines, no deps. Output is universally
// accepted by FFmpeg WASM and the Web Audio AudioPipeline lite path.
function audioBufferToWav(audioBuffer) {
    var numChannels = audioBuffer.numberOfChannels;
    var sampleRate = audioBuffer.sampleRate;
    var numSamples = audioBuffer.length;
    var bytesPerSample = 2;
    var blockAlign = numChannels * bytesPerSample;
    var byteRate = sampleRate * blockAlign;
    var dataSize = numSamples * blockAlign;
    var totalSize = 44 + dataSize;
    var buffer = new ArrayBuffer(totalSize);
    var view = new DataView(buffer);

    function writeStr(offset, str) {
        for (var i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
    }

    writeStr(0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeStr(8, 'WAVE');
    writeStr(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);            // PCM
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, 16, true);
    writeStr(36, 'data');
    view.setUint32(40, dataSize, true);

    // Промежуточный WAV (превью, мост в FFmpeg) — округление без шума;
    // нетронутый звук проходит бит-в-бит (прежняя запись через setInt16 без
    // округления отрезала дробь и меняла до 30 % выборок).
    var pcm = new Int16Array(buffer, 44, numSamples * numChannels);
    var q = pcm16Quantizer(false);
    for (var ch2 = 0; ch2 < numChannels; ch2++) q(audioBuffer.getChannelData(ch2), numSamples, pcm, ch2, numChannels);

    return new Blob([buffer], { type: 'audio/wav' });
}

function withTimeout(promise, ms, errMsg, signal) {
    var t;
    var racers = [
        promise.then(function(v){ clearTimeout(t); return v; }, function(e){ clearTimeout(t); throw e; }),
        new Promise(function(_, rej) {
            t = setTimeout(function() { rej(new Error(errMsg || ('Timed out after ' + ms + 'ms'))); }, ms);
        })
    ];
    if (signal) {
        racers.push(new Promise(function (_, rej) {
            if (signal.aborted) {
                clearTimeout(t);
                rej(new DOMException(
                    (typeof signal.reason === 'string' && signal.reason) || 'AudioRecover decode aborted',
                    'AbortError'
                ));
                return;
            }
            signal.addEventListener('abort', function () {
                clearTimeout(t);
                rej(new DOMException(
                    (typeof signal.reason === 'string' && signal.reason) || 'AudioRecover decode aborted',
                    'AbortError'
                ));
            }, { once: true });
        }));
    }
    return Promise.race(racers);
}

async function readBytes(file) {
    if (window.FileBytes && typeof window.FileBytes.arrayBuffer === 'function') {
        return await window.FileBytes.arrayBuffer(file);
    }
    return await file.arrayBuffer();
}

// Close a decode context without letting the CLOSE itself become the error.
// The old inline `try { ctx.close() } catch(_){}` only ate SYNCHRONOUS
// throws — on old WebKit (iOS 12 Yandex, TIMBRICA-FRONTEND-BH) close() of a
// context in the wrong state REJECTS asynchronously, and that rejection
// escaped every sync catch as an unhandled InvalidStateError. State-guard +
// swallow the promise.
function closeCtxQuiet(ctx) {
    try {
        if (ctx && typeof ctx.close === 'function' && ctx.state !== 'closed') {
            var p = ctx.close();
            if (p && typeof p.catch === 'function') p.catch(function () {});
        }
    } catch (_) {}
}

// Pick a decode context. OfflineAudioContext is preferred — no gesture
// requirement, no audio routing, pure number-crunching. Falls back to
// regular AudioContext where Offline isn't available (very old browsers).
function makeDecodeContext() {
    var Offline = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    if (Offline) {
        // Sample rate / length are placeholders — decodeAudioData ignores them
        // and returns a buffer at the file's actual sample rate.
        try { return { ctx: new Offline(2, 1, 44100), kind: 'offline' }; } catch(_){}
    }
    var Ctx = window.AudioContext || window.webkitAudioContext;
    if (Ctx) {
        try { return { ctx: new Ctx(), kind: 'audio' }; } catch(_){}
    }
    return null;
}

// Public entry: memoizes the FULL outcome (success File or null decline) by
// string identity, so re-wrapped File objects for the same underlying file
// return instantly instead of re-running the whole failing pipeline. The
// telemetry-visible effect: ONE audio_recover_attempted per file per page
// load, not one per user click (Eugenie: 6+ identical attempts / session).
async function tryRepair(file, opts) {
    var memoKey = _fileKey(file);
    if (memoKey && _resultByKey.has(memoKey)) return _resultByKey.get(memoKey);
    var result = await _tryRepairInner(file, opts);
    if (memoKey) {
        if (_resultByKey.size >= 8) { // bounded: drop oldest
            var firstKey = _resultByKey.keys().next().value;
            _resultByKey.delete(firstKey);
        }
        _resultByKey.set(memoKey, result);
    }
    return result;
}

async function _tryRepairInner(file, opts) {
    opts = opts || {};
    if (!file) return null;
    if (!isAudioInput(file)) {
        /* ⚠️ ЗДЕСЬ СТОЯЛ МОЛЧАЛИВЫЙ `return null`, и именно поэтому отказ
           третьего тира видеофайлам прожил незамеченным: наружу уходило
           «AudioRecover declined to retry» без единой строки о причине, и по
           логам отказ по типу файла был неотличим от неудачной попытки
           починки. Отказ, о котором нельзя узнать, почему он случился, — это
           не защита, а слепое пятно. */
        try {
            ae('feature_use', {
                f: 'audio_recover_skipped', reason: 'not_audio_input',
                mime: ((file.type || '') + '').substring(0, 40),
                ext: (((file.name || '').split('.').pop()) || '').toLowerCase().substring(0, 8),
                op: (opts && opts.triggerOp) || 'unknown',
            });
        } catch (_) {}
        return null;
    }

    // Cancellation support — if a signal is passed, abort the recovery on
    // trip. Recovery can take 5-15 s on large files (full decodeAudioData +
    // PCM-to-WAV re-encode); without this the Cancel button left a heavy
    // recovery running in the background.
    var signal = opts.signal || null;
    function _abortIfSignaled(stage) {
        if (signal && signal.aborted) {
            ae('feature_use', { f: 'audio_recover_cancelled', stage: stage, op: opts.triggerOp || 'unknown' });
            throw new DOMException(
                (typeof signal.reason === 'string' && signal.reason) || 'AudioRecover aborted',
                'AbortError'
            );
        }
    }
    _abortIfSignaled('entry');

    if (repairCache && repairCache.has(file)) {
        return repairCache.get(file);
    }

    var fileSizeMB = Math.round((file.size || 0) / 1048576);
    var ext = ((file.name || '').split('.').pop() || '').toLowerCase();
    var triggerOp = opts.triggerOp || 'unknown';
    var origMsg = (opts.origError && (opts.origError.message || opts.origError)) || '';
    origMsg = (origMsg + '').substring(0, 120);

    if ((file.size || 0) > maxInputBytes()) {
        ae('feature_use', { f: 'audio_recover_skipped', reason: 'too_large',
            sizeMB: fileSizeMB, ext: ext, op: triggerOp });
        return null;
    }

    ae('feature_use', { f: 'audio_recover_attempted', op: triggerOp,
        ext: ext, sizeMB: fileSizeMB, err: origMsg });
    stage('audio_recover_start', { ext: ext, sizeMB: fileSizeMB });

    var t0 = Date.now();

    // Early detection: M4A/MP4-family files with NO moov atom anywhere are
    // unrecoverable through any in-browser path (verified empirically in
    // audio-recover-tier4-research.mjs — decodeAudioData / HTMLAudioElement /
    // MediaSource / WebCodecs all fail without sample tables). Emit a specific
    // signal so the caller can show an actionable hint ("file is severely
    // corrupted, try repair-video tool with a healthy reference") instead of
    // an opaque "recovery failed" message. Save 1-3 s by not even attempting
    // decode in this case.
    var isM4aFamily = ['m4a','m4b','mp4','3gp','3gpp','m4r'].indexOf(ext) !== -1
        || (file.type && /\b(mp4|m4a|x-m4a|3gpp)\b/i.test(file.type));
    if (isM4aFamily) {
        try {
            // First confirm the file actually IS an MP4-family container.
            // Misnamed files (WAV/FLAC/MP3 saved with .m4a extension) are
            // routinely encountered — for those, decodeAudioData WILL succeed
            // by content-sniffing, so we must not pre-emptively reject them.
            //
            // Most MP4 files have `ftyp` at offset 4 (size field at [0..3]).
            // BUT some files prepend `wide`/`free`/`skip` placeholder boxes
            // before ftyp (QuickTime streamable convention) — ftyp then lands
            // at offset 12, 20, etc. Scan first 256 bytes for the ftyp ASCII
            // signature so we catch every legitimate MP4 variant.
            // Telemetry 2026-05-17..20: four sessions with ~3 MB M4A files
            // failed because ftyp wasn't at offset 4 — the moov-missing
            // detector was skipped and users got a generic "can't decode"
            // toast instead of the actionable "use /repair-video" hint.
            var head = await file.slice(0, 256).arrayBuffer();
            var actuallyMp4 = containsAscii4cc(head, 'ftyp');
            var moovMissing = actuallyMp4 ? await detectMoovMissing(file) : false;
            if (moovMissing) {
                ae('tool_error', { error_type: 'audio_recover_failed',
                    stage: 'moov_missing_unrecoverable',
                    err: 'file has no moov atom in head or tail — needs reference-based reconstruction',
                    ext: ext, sizeMB: fileSizeMB, op: triggerOp });
                stage('audio_recover_unrecoverable', { reason: 'moov_missing',
                    ext: ext, sizeMB: fileSizeMB });
                showUnrecoverableHint('moov_missing', file);
                return null;
            }
        } catch(_) {
            // Detection failed — fall through to the regular decode attempt.
            // Worst case is a wasted decode; better than dropping a recoverable file.
        }
    }

    var bytes;
    try {
        bytes = await readBytes(file);
    } catch (readErr) {
        ae('tool_error', { error_type: 'audio_recover_failed', stage: 'read',
            err: ((readErr && readErr.message) || '').substring(0, 200),
            ext: ext, sizeMB: fileSizeMB, op: triggerOp });
        return null;
    }

    var dc = makeDecodeContext();
    if (!dc) {
        ae('tool_error', { error_type: 'audio_recover_failed', stage: 'context',
            err: 'no AudioContext available', ext: ext, op: triggerOp });
        return null;
    }

    var decoded;
    var decodeT0 = Date.now();
    try {
        // Some browsers detach the input ArrayBuffer; pass a slice so callers
        // holding the original buffer don't get hit with a NaN-detached view.
        var bytesCopy = bytes.slice(0);
        // Wrap so a SYNCHRONOUS throw (iOS 14 threw "Not enough arguments" for
        // the promise form) also becomes a rejection, and attach a swallow-catch
        // so that if withTimeout's timeout/abort wins the race first, a LATE
        // "invalid state" rejection of the decode can't orphan into an
        // unhandled rejection (TIMBRICA-FRONTEND-BH). The await below still sees
        // the real error via withTimeout's own handler.
        var decodePromise = Promise.resolve().then(function(){ return dc.ctx.decodeAudioData(bytesCopy); });
        decodePromise.catch(function(){});
        decoded = await withTimeout(decodePromise, DECODE_TIMEOUT_MS,
            'Native decode timed out after ' + DECODE_TIMEOUT_MS + 'ms', signal);
    } catch (decErr) {
        closeCtxQuiet(dc.ctx);
        ae('tool_error', { error_type: 'audio_recover_failed', stage: 'decode',
            err: ((decErr && decErr.message) || (decErr && decErr.name) || 'unknown').substring(0, 200),
            origErr: origMsg, ext: ext, sizeMB: fileSizeMB, op: triggerOp,
            ms: Date.now() - t0, ctx_kind: dc.kind });
        // When BOTH FFmpeg AND the browser decoder fail with a container-
        // shape error (the dominant audio-enhancer failure cluster — telemetry
        // 2026-05-22..28 saw `Unable to decode audio data` with FFmpeg origErr
        // `moov atom not found` / `Invalid data found when processing input`
        // accounting for ~70% of audio_recover_failed events), the file is
        // structurally broken — point the user at repair-audio with a
        // one-click transfer instead of dumping a generic "decode failed".
        try {
            var isContainerShaped = CONTAINER_PATTERNS.some(function (re) {
                return re.test(origMsg || '') || re.test((decErr && decErr.message) || '');
            });
            if (isContainerShaped) showUnrecoverableHint('container_broken', file);
        } catch (_) {}
        return null;
    }
    var decodeMs = Date.now() - decodeT0;

    stage('audio_recover_decode_ok', { sampleRate: decoded.sampleRate,
        channels: decoded.numberOfChannels,
        duration_s: Math.round(decoded.duration), ms: decodeMs });

    // Output validation — older Safari and some Firefox builds have shipped
    // silent-decoder bugs where decodeAudioData returns a buffer of all zeros
    // instead of throwing on partial data. Catch that explicitly: if <0.1% of
    // samples are non-zero, treat as decode failure (the user's audio
    // genuinely isn't in there). 1e-5 threshold leaves room for legit silent
    // intros — we only reject FULLY silent buffers.
    try {
        var ch0 = decoded.getChannelData(0);
        var nonZero = 0;
        var peak = 0;
        var len = ch0.length;
        var stride = Math.max(1, Math.floor(len / 4096));  // sample, don't scan all
        var sampled = 0;
        for (var i = 0; i < len; i += stride) {
            sampled++;
            var v = ch0[i];
            if (v < 0) v = -v;
            if (v > 1e-5) nonZero++;
            if (v > peak) peak = v;
        }
        var nonZeroRatio = sampled > 0 ? nonZero / sampled : 0;
        if (nonZeroRatio < 0.001 && len > 1024) {
            closeCtxQuiet(dc.ctx);
            ae('tool_error', { error_type: 'audio_recover_failed', stage: 'silent_output',
                err: 'decoded buffer is silent (peak=' + peak.toExponential(2) + ', non-zero ratio=' + nonZeroRatio.toFixed(5) + ')',
                origErr: origMsg, ext: ext, sizeMB: fileSizeMB, op: triggerOp,
                duration_s: Math.round(decoded.duration), sampleRate: decoded.sampleRate,
                channels: decoded.numberOfChannels });
            return null;
        }
    } catch(_){}

    _abortIfSignaled('post_decode');

    var encodeT0 = Date.now();
    var wavBlob;
    try {
        wavBlob = audioBufferToWav(decoded);
    } catch (encErr) {
        closeCtxQuiet(dc.ctx);
        ae('tool_error', { error_type: 'audio_recover_failed', stage: 'encode',
            err: ((encErr && encErr.message) || '').substring(0, 200),
            ext: ext, sizeMB: fileSizeMB, op: triggerOp });
        return null;
    }
    var encodeMs = Date.now() - encodeT0;

    closeCtxQuiet(dc.ctx);

    var stem = ((file.name || 'audio').replace(/\.[^.\\/]+$/, '')) || 'audio';
    var repairedName = stem + '.recovered.wav';
    var repaired = new File([wavBlob], repairedName, { type: 'audio/wav' });

    if (repairCache) repairCache.set(file, repaired);

    ae('feature_use', { f: 'audio_recover_succeeded', op: triggerOp, ext: ext,
        sizeMB_in: fileSizeMB,
        sizeMB_out: Math.round(repaired.size / 1048576),
        duration_s: Math.round(decoded.duration),
        sampleRate: decoded.sampleRate,
        channels: decoded.numberOfChannels,
        decode_ms: decodeMs,
        encode_ms: encodeMs,
        total_ms: Date.now() - t0 });
    stage('audio_recover_done', { sizeMB_out: Math.round(repaired.size / 1048576),
        ms: Date.now() - t0 });

    return repaired;
}

window.AudioRecover = {
    tryRepair: tryRepair,
    isContainerError: isContainerError,
    isAudioInput: isAudioInput,
    detectMoovMissing: detectMoovMissing,
    /* showUnrecoverableHint exposed so editor tools (trim, volume, etc.) can
       pre-empt their 30 MB FFmpeg dive on detectMoovMissing=true and route
       the user to /repair-audio with the same CTA toast that tryRepair fires
       on its own moov-missing branch. Without this, editors had to either
       duplicate the locale dictionary or skip the actionable UX. */
    showUnrecoverableHint: showUnrecoverableHint,
    audioBufferToWav: audioBufferToWav,  // exposed for unit tests
    _maxInputBytes: maxInputBytes,
    _patterns: CONTAINER_PATTERNS.slice(),
    _resetHintDebounce: function() { hintShownThisSession = false; },  // tests only
    version: '1.1.0'
};

if (typeof window.__drainSharedModule === 'function') window.__drainSharedModule('AudioRecover');

})();
